import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import * as hrmsService from "../../../services/hrmsService";
import AppDropdown from "../../../components/AppDropdown/AppDropdown";
import EmployeeSelect from "../EmployeeSelect";
import { EmptyState, Field, LoadState, PrimaryButton, SecondaryButton } from "../hrComponents";
import { cardStyle, formatDate, formatDateTime, iconBtnStyle, tdStyle, thStyle, useLoad } from "../hrUi";
import { DATE_ORDERS, buildRows, guessColumns, splitText } from "./deviceLogParse";

// Attendance logs exported from a device that is not connected to anything. Choose the file, say which column is which,
// check what would be added, then add it. The punches are ordinary ones (source: device) and are worked into days straight away.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

// Any file the browser can read into rows of cells: Excel, CSV, or the device's own text file.
const readAs = (file, how) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("That file could not be read"));
    reader[how](file);
  });

async function readFile(file) {
  if (/\.(xlsx|xls)$/i.test(file.name)) {
    const wb = XLSX.read(await readAs(file, "readAsArrayBuffer"), { type: "array", raw: true });
    return XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true, defval: "" });
  }
  return splitText(await readAs(file, "readAsText"));
}

const colOptions = (aoa, hasHeader, blank) => {
  const width = Math.max(0, ...aoa.slice(0, 20).map((r) => r.length));
  return [
    ...(blank ? [{ label: blank, value: -1 }] : []),
    ...Array.from({ length: width }, (_, i) => ({ label: hasHeader && aoa[0][i] !== "" ? `${i + 1}: ${aoa[0][i]}` : `Column ${i + 1}`, value: i })),
  ];
};

function Mapping({ aoa, mapping, onChange }) {
  const opts = colOptions(aoa, mapping.hasHeader);
  const optsBlank = colOptions(aoa, mapping.hasHeader, "Not in the file");
  const split = mapping.datetime === null;
  const sample = aoa.slice(mapping.hasHeader ? 1 : 0, (mapping.hasHeader ? 1 : 0) + 4);
  return (
    <div>
      <div className="form-check mb-3">
        <input id="dl-header" type="checkbox" className="form-check-input" checked={mapping.hasHeader} onChange={(e) => onChange({ hasHeader: e.target.checked })} />
        <label htmlFor="dl-header" className="form-check-label" style={{ fontSize: 14 }}>
          The first line is a heading
        </label>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0 16px" }}>
        <Field label="Person's number on the device" required>
          <AppDropdown value={mapping.user} options={opts} onChange={(e) => onChange({ user: e.value })} />
        </Field>
        <Field label="Date order" hint="Only matters for dates like 05/10/2026">
          <AppDropdown value={mapping.order} options={DATE_ORDERS} onChange={(e) => onChange({ order: e.value })} />
        </Field>
        <Field label="Date and time are in">
          <AppDropdown
            value={split ? "SPLIT" : "ONE"}
            options={[{ label: "One column", value: "ONE" }, { label: "Two columns (date, time)", value: "SPLIT" }]}
            onChange={(e) => onChange(e.value === "SPLIT" ? { datetime: null, date: 0, time: 1 } : { datetime: 1, date: null, time: null })}
          />
        </Field>
        {split ? (
          <>
            <Field label="Date column">
              <AppDropdown value={mapping.date} options={opts} onChange={(e) => onChange({ date: e.value })} />
            </Field>
            <Field label="Time column">
              <AppDropdown value={mapping.time} options={opts} onChange={(e) => onChange({ time: e.value })} />
            </Field>
          </>
        ) : (
          <Field label="Date and time column" required>
            <AppDropdown value={mapping.datetime} options={opts} onChange={(e) => onChange({ datetime: e.value })} />
          </Field>
        )}
        <Field label="In / Out column" hint="Leave as 'Not in the file' unless the device records it reliably. Otherwise punches are paired in order: in, out, in, out.">
          <AppDropdown value={mapping.direction === null ? -1 : mapping.direction} options={optsBlank} onChange={(e) => onChange({ direction: e.value === -1 ? null : e.value })} />
        </Field>
      </div>
      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>First lines of the file:</div>
      <div style={{ ...cardStyle, overflowX: "auto" }}>
        <table className="table mb-0" style={{ fontSize: 12 }}>
          <tbody>
            {sample.map((r, i) => (
              <tr key={i}>
                {r.slice(0, 8).map((c, j) => (
                  <td key={j} style={{ ...tdStyle, padding: "6px 10px" }}>
                    {String(c)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div style={{ ...cardStyle, padding: "10px 14px", flex: "1 1 130px" }}>
      <div style={{ fontSize: 12, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: tone || "#111827" }}>{value}</div>
    </div>
  );
}

function History() {
  const imports = useLoad(() => hrmsService.listDeviceLogImports(), []);
  const users = useLoad(() => hrmsService.listDeviceUsers(), []);
  const remove = async (id) => {
    try {
      await hrmsService.removeDeviceUser(id);
      users.reload();
    } catch (err) {
      toast.error("Could not remove it");
    }
  };
  return (
    <>
      <h6 className="mt-4 mb-2">Earlier uploads</h6>
      <LoadState loading={imports.loading} error={imports.error} onRetry={imports.reload} rows={2}>
        {(imports.data || []).length === 0 ? (
          <EmptyState title="Nothing uploaded yet" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640, fontSize: 13 }}>
              <thead>
                <tr>
                  {["When", "File", "Days", "Added", "Repeats", "Not used"].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {imports.data.map((i) => (
                  <tr key={i.id}>
                    <td style={tdStyle}>{formatDateTime(i.created_at)}</td>
                    <td style={tdStyle}>
                      {i.file_name || "—"}
                      {i.device_label ? <div style={{ fontSize: 12, color: "#6B7280" }}>{i.device_label}</div> : null}
                    </td>
                    <td style={tdStyle}>{i.date_from ? `${formatDate(i.date_from)} – ${formatDate(i.date_to)}` : "—"}</td>
                    <td style={tdStyle}>{i.punches_added}</td>
                    <td style={tdStyle}>{i.duplicates}</td>
                    <td style={tdStyle}>{Number(i.rejected) + Number(i.unmatched)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      {(users.data || []).length ? (
        <>
          <h6 className="mt-4 mb-2">Device numbers remembered</h6>
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ fontSize: 13 }}>
              <tbody>
                {users.data.map((u) => (
                  <tr key={u.id}>
                    <td style={tdStyle}>{u.device_user}</td>
                    <td style={tdStyle}>
                      {u.full_name} <span style={{ color: "#6B7280" }}>({u.employee_code})</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={linkBtn} onClick={() => remove(u.id)}>
                        Forget
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  );
}

export default function DeviceLogsTab() {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [aoa, setAoa] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [label, setLabel] = useState("");
  const [picked, setPicked] = useState({}); // device number -> employee id, for numbers the company does not know yet
  const [remember, setRemember] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  const reset = () => {
    setFile(null);
    setAoa(null);
    setMapping(null);
    setPreview(null);
    setPicked({});
    setResult(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const choose = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setResult(null);
    setPreview(null);
    setPicked({});
    try {
      const data = await readFile(f);
      if (!data.length) throw new Error("The file is empty");
      setFile(f);
      setAoa(data);
      setMapping({ ...guessColumns(data), order: "DMY" });
    } catch (err) {
      toast.error(err.message || "That file could not be read");
      reset();
    }
  };

  const change = (patch) => {
    setMapping((m) => ({ ...m, ...patch }));
    setPreview(null);
  };
  const rows = () => buildRows(aoa, mapping);
  const mappings = () => Object.entries(picked).filter(([, id]) => id).map(([device_user, employee_id]) => ({ device_user, employee_id }));

  const check = async () => {
    setBusy(true);
    try {
      const res = await hrmsService.previewDeviceLogs({ rows: rows(), mappings: mappings() });
      if (res?.success) setPreview(res.data);
      else toast.error(hrmsService.responseMessage(res));
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    setBusy(true);
    try {
      const res = await hrmsService.commitDeviceLogs({ rows: rows(), mappings: mappings(), save_mappings: remember, file_name: file.name, device_label: label || undefined });
      if (res?.success) {
        toast.success(res.message || "Added");
        setResult(res.data);
        setHistoryKey((k) => k + 1);
        setPreview(null);
        setAoa(null);
        setMapping(null);
        setFile(null);
        if (fileInput.current) fileInput.current.value = "";
      } else toast.error(hrmsService.responseMessage(res));
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12, maxWidth: 720 }}>
        For a device that is not connected to anything: export its attendance log (Excel, CSV or the device's own file), then upload it here. The times are read as your company's clock. Days that are already locked cannot be changed.
      </div>

      {result ? (
        <div className="mb-3" style={{ background: "#D1FAE5", borderRadius: 8, padding: "10px 14px", fontSize: 14 }} role="status">
          Added {result.added} punch{result.added === 1 ? "" : "es"} for {result.people} {result.people === 1 ? "person" : "people"}
          {result.duplicates ? `, ${result.duplicates} already there` : ""}
          {result.rejected || result.unmatched_rows ? `, ${Number(result.rejected) + Number(result.unmatched_rows)} not used` : ""}. The days have been worked out again.
        </div>
      ) : null}

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv,.txt,.dat" onChange={choose} aria-label="Device log file" className="form-control" style={{ maxWidth: 360 }} />
        {file ? <SecondaryButton onClick={reset}>Start again</SecondaryButton> : null}
      </div>

      {aoa && mapping ? (
        <>
          <div style={{ ...cardStyle, padding: 16, marginBottom: 16 }}>
            <Mapping aoa={aoa} mapping={mapping} onChange={change} />
            <Field label="Which device is this? (optional)" hint="Kept with the punches, e.g. Main gate">
              <input className="form-control" maxLength={64} value={label} onChange={(e) => setLabel(e.target.value)} style={{ maxWidth: 320 }} />
            </Field>
            <PrimaryButton onClick={check} disabled={busy}>
              Check the file
            </PrimaryButton>
          </div>

          {preview ? (
            <div data-testid="device-preview">
              <div className="d-flex flex-wrap gap-3 mb-3">
                <Stat label="Lines in the file" value={preview.rows} />
                <Stat label="Punches to add" value={preview.to_add} tone="#065F46" />
                <Stat label="People" value={preview.people} />
                {preview.duplicates ? <Stat label="Already there" value={preview.duplicates} /> : null}
                {preview.rejected ? <Stat label="Cannot be used" value={preview.rejected} tone="#991B1B" /> : null}
              </div>
              {preview.date_from ? (
                <div style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
                  {formatDate(preview.date_from)} to {formatDate(preview.date_to)}
                </div>
              ) : null}

              {preview.unmatched.length ? (
                <div style={{ ...cardStyle, padding: 16, marginBottom: 16 }}>
                  <strong style={{ fontSize: 14 }}>These numbers are not an employee code</strong>
                  <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 10 }}>Choose who each one is, then check the file again. Numbers left blank are skipped.</div>
                  {preview.unmatched.map((u) => (
                    <div key={u.device_user} className="d-flex flex-wrap gap-3 align-items-center mb-2">
                      <div style={{ width: 160, fontSize: 14 }}>
                        <strong>{u.device_user}</strong> <span style={{ color: "#6B7280" }}>({u.count} lines)</span>
                      </div>
                      <div style={{ minWidth: 260 }}>
                        <EmployeeSelect value={picked[u.device_user]} onChange={(id) => { setPicked((p) => ({ ...p, [u.device_user]: id })); setPreview(null); }} placeholder="Who is this?" />
                      </div>
                    </div>
                  ))}
                  <div className="form-check mt-2">
                    <input id="dl-remember" type="checkbox" className="form-check-input" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    <label htmlFor="dl-remember" className="form-check-label" style={{ fontSize: 14 }}>
                      Remember these for next time
                    </label>
                  </div>
                </div>
              ) : null}

              {preview.rejects.length ? (
                <div style={{ ...cardStyle, padding: 16, marginBottom: 16 }}>
                  <strong style={{ fontSize: 14 }}>Lines that cannot be used</strong>
                  <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Line numbers count from the first line after the heading.</div>
                  {preview.rejects.map((r) => (
                    <div key={r.row} style={{ fontSize: 13 }}>
                      Line {r.row}: {r.reason}
                    </div>
                  ))}
                </div>
              ) : null}

              <PrimaryButton onClick={add} disabled={busy || (preview.to_add === 0)}>
                Add {preview.to_add} punch{preview.to_add === 1 ? "" : "es"}
              </PrimaryButton>
            </div>
          ) : null}
        </>
      ) : null}

      <History key={historyKey} />
    </div>
  );
}
